import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper } from 'pdftron';
import { PDFTronTools } from 'types';
import { DocumentTypes, Difference, DifferenceTypeNames } from 'types';
import { getDifferences, getInspectionId } from 'store';
import { getSelectedTool } from 'store/inspection/inspectionSelectors';
import store from 'store/store';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { createMergedDifference } from 'store/request/differences/actions';
import { AnnotationAction } from 'components/PDFViewer/Utils';
import { trackEvent } from 'components/Tracker/TrackerProvider';
import { getTrackerMetadata } from 'store/trackerMetadata/trackerMetadata';
import selectTool from 'utils/selectTool/selectTool';

class MergeGraphicsZone {
  source: WebViewerWrapper | null = null;
  target: WebViewerWrapper | null = null;

  // Phase 2: Simple auto-matching using existing patterns
  areFileDimensionsEqual = true;

  // Flag to prevent infinite loop in auto-matching
  private isCreatingAutoMatch = false;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
    this.updateAreFileDimensionsEqual();
    this.createMergeZoneAnnotationTools();
  }

  /**
   * Update whether source and target file are equal in dimensions and number of pages
   * (Reused from GraphicZone pattern)
   */
  updateAreFileDimensionsEqual() {
    if (this.source && this.target) {
      const sourceTotal = this.source.page.getTotalPages();
      const targetTotal = this.target.page.getTotalPages();
      const sourceTotalSize = this.source.page.getTotalPageSize();
      const targetTotalSize = this.target.page.getTotalPageSize();
      if (sourceTotal !== targetTotal) {
        this.areFileDimensionsEqual = false;
      } else {
        this.areFileDimensionsEqual = sourceTotalSize.every(
          (pageSize, index) =>
            pageSize.height === targetTotalSize[index].height && pageSize.width === targetTotalSize[index].width,
        );
      }
    }
  }

  /**
   * Check if a difference bounding box is fully covered by the merge zone
   */
  private boundingBoxFullyCovered(
    mergeZone: { x: number; y: number; width: number; height: number },
    difference: { x: number; y: number; width: number; height: number },
  ): boolean {
    // Calculate the boundaries of both boxes
    const mergeLeft = mergeZone.x;
    const mergeRight = mergeZone.x + mergeZone.width;
    const mergeTop = mergeZone.y;
    const mergeBottom = mergeZone.y + mergeZone.height;

    const diffLeft = difference.x;
    const diffRight = difference.x + difference.width;
    const diffTop = difference.y;
    const diffBottom = difference.y + difference.height;

    // Check if the difference is fully contained within the merge zone
    return diffLeft >= mergeLeft && diffRight <= mergeRight && diffTop >= mergeTop && diffBottom <= mergeBottom;
  }

  /**
   * Check if a difference is a graphics difference (Graphics, EmbeddedImage, or merged graphics)
   */
  private isGraphicsDifference(difference: Difference): boolean {
    return difference.type === DifferenceTypeNames.Graphics;
  }

  /**
   * Check if a difference is a merged graphics difference
   */
  private isMergedGraphicsDifference(difference: Difference): boolean {
    if (!this.isGraphicsDifference(difference)) return false;
    return !!(difference.originalDifferenceIds && difference.originalDifferenceIds.length > 0);
  }

  /**
   * Find graphics differences that are fully covered by the merge zone
   * This includes regular graphics differences and previously merged graphics differences
   * Note: When merged, the original differences will be marked as excluded and hidden from the list
   */
  private findOverlappingDifferences(
    zone: Core.Annotations.Annotation,
    sourceDocumentType: DocumentTypes,
  ): Difference[] {
    const state = store.getState();
    const allDifferences = getDifferences(state);

    // Filter for graphics differences (includes both regular and merged graphics differences)
    // Merged graphics differences can be merged again into larger groups
    const graphicsDifferences = allDifferences.filter(this.isGraphicsDifference);

    const mergeZoneBounds = {
      x: zone.X,
      y: zone.Y,
      width: zone.Width,
      height: zone.Height,
    };

    const overlappingDifferences = graphicsDifferences.filter((diff) => {
      // Get the location data for the source document type
      const diffLocation = sourceDocumentType === DocumentTypes.source ? diff.source.location : diff.target.location;

      // Check if difference is on the same page
      if (diffLocation.pageNumber !== zone.PageNumber) {
        return false;
      }

      // Convert difference rect [x, y, width, height] to bounding box
      const diffBounds = {
        x: diffLocation.rect[0],
        y: diffLocation.rect[1],
        width: diffLocation.rect[2],
        height: diffLocation.rect[3],
      };

      return this.boundingBoxFullyCovered(mergeZoneBounds, diffBounds);
    });

    return overlappingDifferences;
  }

  /**
   * Calculate smart positioning based on difference relationships
   */
  private calculateSmartPosition(
    zone: Core.Annotations.Annotation,
    sourceDocumentType: DocumentTypes,
    overlappingDifferences: Difference[],
  ): { x: number; y: number; pageNumber: number } | null {
    if (overlappingDifferences.length === 0) {
      return null;
    }

    // Use the first difference as the reference point
    const referenceDiff = overlappingDifferences[0];

    // Determine which document the zone was drawn on and where it should be placed
    const sourceDiffLocation = referenceDiff.source.location;
    const targetDiffLocation = referenceDiff.target.location;

    // Helper function to get the actual top-left corner of a rectangle, accounting for negative width/height
    const getRectBounds = (rect: number[]) => {
      const [x, y, width, height] = rect;
      return {
        left: width < 0 ? x + width : x,
        top: height < 0 ? y + height : y,
        right: width < 0 ? x : x + width,
        bottom: height < 0 ? y : y + height,
        width: Math.abs(width),
        height: Math.abs(height),
      };
    };

    const sourceBounds = getRectBounds(sourceDiffLocation.rect);
    const targetBounds = getRectBounds(targetDiffLocation.rect);

    let offsetX: number;
    let offsetY: number;
    let smartX: number;
    let smartY: number;
    let targetPageNumber: number;

    if (sourceDocumentType === DocumentTypes.source) {
      // User drew on SOURCE document, place matching zone on TARGET document
      offsetX = zone.X - sourceBounds.left;
      offsetY = zone.Y - sourceBounds.top;
      smartX = targetBounds.left + offsetX;
      smartY = targetBounds.top + offsetY;
      targetPageNumber = targetDiffLocation.pageNumber;
    } else {
      // User drew on TARGET document, place matching zone on SOURCE document
      offsetX = zone.X - targetBounds.left;
      offsetY = zone.Y - targetBounds.top;
      smartX = sourceBounds.left + offsetX;
      smartY = sourceBounds.top + offsetY;
      targetPageNumber = sourceDiffLocation.pageNumber;
    }

    return {
      x: smartX,
      y: smartY,
      pageNumber: targetPageNumber,
    };
  }

  /**
   * Smart merge zone positioning based on difference relationships
   * @param zone Annotation to be duplicated
   * @param targetInstance The target instance
   * @param sourceDocumentType The source document type
   */
  private duplicateMergeZone(
    zone: Core.Annotations.Annotation,
    targetInstance: WebViewerInstance,
    sourceDocumentType: DocumentTypes,
  ) {
    const annotationManager = targetInstance.Core.documentViewer.getAnnotationManager();

    // Create target zone annotation
    const targetZone = new (this.createMergeZoneAnnotation(targetInstance))();
    targetZone.ReadOnly = true;
    targetZone.Width = zone.Width;
    targetZone.Height = zone.Height;
    // Note: PDFTron will automatically generate a unique ID for the new annotation

    // Step 1: Find differences that overlap with the merge zone
    const overlappingDifferences = this.findOverlappingDifferences(zone, sourceDocumentType);

    // Step 2: Calculate smart position based on difference relationships
    const smartPosition = this.calculateSmartPosition(zone, sourceDocumentType, overlappingDifferences);

    if (smartPosition) {
      // Use smart positioning based on difference relationships
      targetZone.X = smartPosition.x;
      targetZone.Y = smartPosition.y;
      targetZone.PageNumber = Math.min(smartPosition.pageNumber, targetInstance.Core.documentViewer.getPageCount());
    } else {
      // Fallback to naive positioning
      targetZone.X = this.areFileDimensionsEqual ? zone.X : 0;
      targetZone.Y = this.areFileDimensionsEqual ? zone.Y : 0;
      targetZone.PageNumber = Math.min(zone.PageNumber, targetInstance.Core.documentViewer.getPageCount());
    }

    annotationManager.addAnnotation(targetZone);
    annotationManager.redrawAnnotation(targetZone);

    return targetZone;
  }

  createMergeZoneAnnotation(instance: WebViewerInstance) {
    // eslint-disable-next-line max-classes-per-file
    class MergeZoneAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor() {
        super();
        this.ToolName = PDFTronTools.MERGE_GRAPHICS_ZONE;
        this.IsHoverable = false; // Disable hover to prevent styling interference
        this.disableRotationControl();
        this.setCustomData('isMergeZone', 'true'); // Mark as merge zone for cleanup

        // Styling is now handled entirely by the draw method with hardcoded values
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        // Completely skip setStyles and any PDFTron styling to maintain consistent transparency
        // This ensures hover states don't interfere with our custom styling

        // Save context state
        ctx.save();

        // Draw cyan selection rectangle with explicit transparency
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);

        // Force cyan colors with consistent transparency regardless of hover state
        ctx.strokeStyle = 'rgba(0, 255, 255, 1)'; // Cyan border (solid)
        ctx.fillStyle = 'rgba(0, 255, 255, 0.3)'; // Cyan fill with 30% opacity
        ctx.lineWidth = 2;

        // Always use the same styling regardless of annotation state
        ctx.fill(annotationRectangle);
        ctx.stroke(annotationRectangle);

        // Restore context state
        ctx.restore();
      }
    }
    return MergeZoneAnnotation;
  }

  createMergeZoneAnnotationTools() {
    if (this.source && this.target) {
      [this.source, this.target].forEach((wrapper: WebViewerWrapper, index) => {
        const { instance } = wrapper;
        const docType = index === 0 ? 'source' : 'target';

        // eslint-disable-next-line max-classes-per-file
        const toolObject = new (class MergeZoneCreateTool extends instance.Core.Tools.RectangleCreateTool {
          constructor(mergeZoneAnnotation: any) {
            super(instance.Core.documentViewer);
            instance.Core.Tools.GenericAnnotationCreateTool.call(
              this,
              instance.Core.documentViewer,
              mergeZoneAnnotation,
            );
          }

          // switchIn is called when this tool is selected
          switchIn(previousTool: any) {
            super.switchIn(previousTool);
            // Set crosshair cursor
            this.cursor = 'crosshair';

            // Add manual-tool-mode to force crosshair and disable annotation hover
            const viewerElement = instance.Core.documentViewer.getScrollViewElement();
            if (viewerElement) {
              viewerElement.classList.add('manual-tool-mode');
            }
          }

          mouseLeftDown(e: any) {
            // Only allow drawing if merge graphics zone tool is selected
            const state = store.getState();
            const selectedTool = getSelectedTool(state);

            if (selectedTool !== PDFTronTools.MERGE_GRAPHICS_ZONE) {
              return;
            }

            super.mouseLeftDown(e);
          }

          mouseMove(e: any) {
            const state = store.getState();
            const selectedTool = getSelectedTool(state);

            if (selectedTool !== PDFTronTools.MERGE_GRAPHICS_ZONE) {
              return;
            }

            super.mouseMove(e);
          }

          mouseLeftUp(e: any) {
            const state = store.getState();
            const selectedTool = getSelectedTool(state);

            if (selectedTool !== PDFTronTools.MERGE_GRAPHICS_ZONE) {
              return;
            }

            super.mouseLeftUp(e);
          }

          // switchOut is called when switching away from this tool
          switchOut(newTool: any) {
            super.switchOut(newTool);
            const viewerElement = instance.Core.documentViewer.getScrollViewElement();
            if (viewerElement) {
              viewerElement.classList.remove('manual-tool-mode');
            }
          }
        })(this.createMergeZoneAnnotation(instance));

        wrapper.registerTool({
          toolName: PDFTronTools.MERGE_GRAPHICS_ZONE,
          toolObject,
          buttonImage:
            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTcgMTRMMTIgOUwxNyAxNCIgc3Ryb2tlPSIjMzMzIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNNyA5TDEyIDRMMTcgOSIgc3Ryb2tlPSIjMzMzIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8cGF0aCBkPSJNMTIgNFYxNiIgc3Ryb2tlPSIjMzMzIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K',
        });
      });
    }
  }

  /**
   * Helper method to create location object from zone bounds
   */
  private createLocationFromBounds(zoneBounds: {
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }) {
    return {
      pageNumber: zoneBounds.pageNumber,
      rect: [zoneBounds.x, zoneBounds.y, zoneBounds.width, zoneBounds.height],
    };
  }

  /**
   * Helper method to construct merge data with proper location assignment
   */
  private buildMergeData(
    inspectionId: string,
    overlappingGraphicsDifferences: Difference[],
    documentType: DocumentTypes,
    originalZoneBounds: { pageNumber: number; x: number; y: number; width: number; height: number },
    matchingZoneBounds: { pageNumber: number; x: number; y: number; width: number; height: number },
  ) {
    const isSourceDocument = documentType === DocumentTypes.source;

    return {
      inspectionId,
      originalDifferenceIds: overlappingGraphicsDifferences.map((diff) => diff.id),
      sourceLocation: this.createLocationFromBounds(isSourceDocument ? originalZoneBounds : matchingZoneBounds),
      targetLocation: this.createLocationFromBounds(isSourceDocument ? matchingZoneBounds : originalZoneBounds),
    };
  }

  // called from docManager/EventHandlers
  mergeGraphicsZoneAnnotationChanged(
    documentType: DocumentTypes,
    annotation: Core.Annotations.Annotation,
    action: string,
  ) {
    // Prevent infinite loop - if we're already creating an auto-match, don't process this event
    if (this.isCreatingAutoMatch) {
      return;
    }

    if (action === AnnotationAction.ADD) {
      // Validate that there are at least 2 graphics differences under the zone
      // This supports merging regular graphics differences and re-merging previously merged differences
      const overlappingGraphicsDifferences = this.findOverlappingDifferences(annotation, documentType);

      if (overlappingGraphicsDifferences.length < 2) {
        // Remove the zone since it's not valid
        const instance = documentType === DocumentTypes.source ? this.source?.instance : this.target?.instance;
        if (instance) {
          const annotationManager = instance.Core.documentViewer.getAnnotationManager();
          annotationManager.deleteAnnotation(annotation);
        }

        // Show user feedback with specific error message
        const { setDocumentToast } = useDocumentToastStateStore.getState();
        const errorMessage =
          overlappingGraphicsDifferences.length === 0
            ? 'Merge Graphics Zone requires graphics differences beneath the bounding box. No zone created.'
            : 'Merge Graphics Zone requires at least 2 graphics differences to merge. No zone created.';

        setDocumentToast(documentType, {
          message: errorMessage,
          type: 'error',
          duration: 5000,
        });

        return; // Exit early - don't process auto-matching
      }

      // Phase 2: Smart auto-matching based on difference relationships
      const oppositeInstance = documentType === DocumentTypes.source ? this.target?.instance : this.source?.instance;

      if (oppositeInstance) {
        // Set flag to prevent recursive calls
        this.isCreatingAutoMatch = true;

        try {
          // Create matching zone on opposite document
          const matchingZone = this.duplicateMergeZone(annotation, oppositeInstance, documentType);

          // Phase 3 - Implement merge processing
          const inspectionId = getInspectionId(store.getState());
          if (inspectionId && overlappingGraphicsDifferences.length > 0) {
            // Get the zone bounds from the original annotation
            const originalZoneBounds = {
              x: annotation.X,
              y: annotation.Y,
              width: annotation.Width,
              height: annotation.Height,
              pageNumber: annotation.PageNumber,
            };

            // Get the zone bounds from the matching zone
            const matchingZoneBounds = {
              x: matchingZone.X,
              y: matchingZone.Y,
              width: matchingZone.Width,
              height: matchingZone.Height,
              pageNumber: matchingZone.PageNumber,
            };

            // Create the merge data with correct coordinates for each document
            // Note: The backend will calculate optimal bounding boxes from the contained annotations
            // instead of using these zone bounds directly, so the merged annotation will snap to
            // the outer edges of the contained annotations for better user experience
            const mergeData = this.buildMergeData(
              inspectionId,
              overlappingGraphicsDifferences,
              documentType,
              originalZoneBounds,
              matchingZoneBounds,
            );

            // Dispatch the merge action - the saga will handle both annotation redraw and cleanup
            store.dispatch(createMergedDifference(mergeData));

            // Switch back to PAN tool after successful merge (one-shot behavior)
            selectTool(PDFTronTools.PAN);
          }
        } finally {
          // Always reset the flag, even if there's an error
          this.isCreatingAutoMatch = false;
        }
      }
    }
  }

  // called from docManager/EventHandlers
  mergeGraphicsZoneAnnotationSelected(
    documentType: DocumentTypes,
    annotation: Core.Annotations.Annotation,
    action: string,
  ) {
    // Selection behavior for merge graphics zones - currently no special handling needed
  }
}

export default MergeGraphicsZone;
